<template>
  <div class="b-wrap" v-loading="loading">
    <div class="hero">
      <h2>{{ shopName }} · 我的归属代理</h2>
      <p>平台按佣金规则向代理支付分润，不影响您的货款结算。改绑/解绑需平台审核。</p>
    </div>

    <el-card v-if="list.length" shadow="never" class="mt-16px">
      <template #header>归属信息</template>
      <div v-for="item in list" :key="item.id" class="agent-row">
        <div class="av">{{ (item.agentName || '代').slice(0, 1) }}</div>
        <div class="flex-1">
          <div class="name">
            {{ item.agentName || '-' }}
            <el-tag size="small" class="ml-8px">{{ softwareLabel(item.softwareId) }}</el-tag>
          </div>
          <div class="sub">
            区域：{{ item.regionName || '-' }} · 编号：{{ item.agentNo || item.agentId }} ·
            {{ item.confirmed ? '已确认' : '待确认' }}
          </div>
        </div>
        <dict-tag :type="DICT_TYPE.AGENT_CHANNEL_BIND_MODE" :value="item.bindMode" />
        <div class="ops">
          <el-button class="op-btn op-rebind" size="small" @click="openChange(item, 1)">
            <Icon icon="ep:switch" class="mr-4px" />申请改绑
          </el-button>
          <el-button class="op-btn op-unbind" size="small" @click="openChange(item, 2)">
            <Icon icon="ep:link" class="mr-4px" />申请解绑
          </el-button>
        </div>
      </div>
    </el-card>

    <el-card v-else shadow="never" class="mt-16px">
      <el-empty description="您尚未归属任何代理商">
        <el-button type="primary" @click="$router.push('/store/belonging/confirm')">前往归属确认</el-button>
      </el-empty>
    </el-card>

    <el-card shadow="never" class="mt-16px">
      <template #header>我的变更申请</template>
      <el-table :data="changeList" stripe size="small">
        <el-table-column label="软件" prop="softwareId" width="100" />
        <el-table-column label="类型" width="100">
          <template #default="{ row }">
            <dict-tag :type="DICT_TYPE.AGENT_CHANNEL_REL_CHANGE_TYPE" :value="row.changeType" />
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <dict-tag :type="DICT_TYPE.AGENT_CHANNEL_REL_CHANGE_STATUS" :value="row.status" />
          </template>
        </el-table-column>
        <el-table-column label="原因" prop="reason" min-width="140" show-overflow-tooltip />
        <el-table-column label="时间" prop="createTime" min-width="170" :formatter="dateFormatter" />
      </el-table>
    </el-card>

    <el-dialog v-model="changeVisible" :title="changeForm.changeType === 1 ? '申请改绑' : '申请解除归属'" width="460px">
      <el-form label-width="100px">
        <el-form-item label="软件">{{ softwareLabel(changeForm.softwareId) }}</el-form-item>
        <el-form-item v-if="changeForm.changeType === 1" label="目标代理ID" required>
          <el-input v-model="changeForm.targetAgentId" placeholder="填写目标代理商档案 ID" />
        </el-form-item>
        <el-form-item label="原因">
          <el-input v-model="changeForm.reason" type="textarea" rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="changeVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitChange">提交申请</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { applyBelongingChange, getBelongingChangeList, getBelongingList } from '@/api/agent/belonging'
import { getShopInfo } from '@/api/merchant/shop'
import { getMerchantSession } from '@/views/merchant/registry/merchant-session'
import { DICT_TYPE, getDictLabel } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import { ElMessage } from 'element-plus'

defineOptions({ name: 'MerchantBelongingHome' })

const loading = ref(false)
const saving = ref(false)
const list = ref<any[]>([])
const changeList = ref<any[]>([])
const shopName = ref('我的店铺')
const shopId = ref<number>()
const changeVisible = ref(false)
const changeForm = reactive({
  softwareId: '',
  changeType: 1,
  targetAgentId: '',
  reason: ''
})

const softwareLabel = (id?: string) => getDictLabel(DICT_TYPE.AGENT_CHANNEL_SOFTWARE, id) || id || '-'

const resolveShopId = async () => {
  // 优先会话里商城模块 subjectId（店铺 ID），避免依赖 shop:info:query
  const sess = getMerchantSession()
  const mall = sess?.modules?.find((m) => m.moduleId === 'MALL')
  if (mall?.subjectId) {
    const sid = Number(mall.subjectId)
    if (sid) shopId.value = sid
  }
  shopName.value = sess?.shopName || sess?.merchantName || '我的店铺'
  try {
    const shop = await getShopInfo(true)
    if (shop?.id) shopId.value = shop.id
    if (shop?.shopName) shopName.value = shop.shopName
  } catch {
    if (!shopId.value) {
      const sid = localStorage.getItem('merchantShopId')
      shopId.value = sid ? Number(sid) : undefined
    }
  }
}

const load = async () => {
  loading.value = true
  try {
    await resolveShopId()
    if (shopId.value) {
      list.value = (await getBelongingList(shopId.value)) || []
      changeList.value = (await getBelongingChangeList(shopId.value)) || []
    }
  } finally {
    loading.value = false
  }
}

const openChange = (item: any, changeType: number) => {
  changeForm.softwareId = item.softwareId
  changeForm.changeType = changeType
  changeForm.targetAgentId = ''
  changeForm.reason = ''
  changeVisible.value = true
}

const submitChange = async () => {
  if (!shopId.value) return
  if (changeForm.changeType === 1 && !changeForm.targetAgentId) {
    ElMessage.warning('请填写目标代理 ID')
    return
  }
  saving.value = true
  try {
    await applyBelongingChange({
      merchantId: shopId.value,
      softwareId: changeForm.softwareId,
      changeType: changeForm.changeType,
      targetAgentId: changeForm.targetAgentId ? Number(changeForm.targetAgentId) : undefined,
      reason: changeForm.reason
    })
    ElMessage.success('已提交，等待平台审核')
    changeVisible.value = false
    await load()
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.b-wrap { max-width: 860px; margin: 0 auto; }
.hero {
  background: linear-gradient(135deg, #102a4c, #1c3e6e);
  border-radius: 14px; color: #fff; padding: 26px 30px;
}
.hero h2 { font-size: 20px; margin: 0 0 6px; }
.hero p { margin: 0; color: #cdd9ec; font-size: 13px; }
.agent-row {
  display: flex; align-items: center; gap: 14px;
  padding: 14px 0; border-bottom: 1px solid var(--el-border-color-lighter);
}
.av {
  width: 44px; height: 44px; border-radius: 50%;
  background: #0e9c8d; color: #fff; display: flex; align-items: center; justify-content: center;
  font-weight: 700;
}
.name { font-weight: 700; font-size: 15px; }
.sub { margin-top: 4px; font-size: 12px; color: var(--el-text-color-secondary); }
.flex-1 { flex: 1; min-width: 0; }
.ops {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  flex-shrink: 0;
}
.op-btn {
  margin: 0 !important;
  min-width: 108px;
  border-radius: 8px;
  font-weight: 500;
}
.op-rebind {
  color: #1c3e6e;
  background: #eef3fb;
  border-color: #d5e0f2;
}
.op-rebind:hover {
  color: #fff;
  background: #1c3e6e;
  border-color: #1c3e6e;
}
.op-unbind {
  color: #c45656;
  background: #fef0f0;
  border-color: #f5c6c6;
}
.op-unbind:hover {
  color: #fff;
  background: #c45656;
  border-color: #c45656;
}
</style>
